import { dayInTz, timeInTz } from '../../lib/format';

/**
 * The crew map's "as of", saying how OLD what is on screen is (ops insights Phase 4, Q2).
 *
 * 🔴 The read is persisted for 24 h like every other, so a laptop opened in the morning can
 * draw yesterday evening's answer before the first refetch lands, and "As of 10:42 PM" alone
 * reads as live. So the line carries the honest age:
 *  - the DATE, when the read was not taken on the shop's today;
 *  - "N min ago" once it is older than two refreshes would make it (`STALE_AFTER_MS`);
 *  - "Offline" when no refresh can go out, and "Refreshing…" while one is on the wire.
 *
 * Pure, with the clock passed in, so each case is a test rather than a wait.
 */
export const STALE_AFTER_MS = 2 * 60_000;

export function asOfLine({
  asOf,
  now,
  tz,
  offline,
  fetching,
}: {
  asOf: Date | string;
  now: Date | number;
  tz: string;
  /** No refresh can go out: the device is offline, or the last one is paused for it. */
  offline: boolean;
  /** A refresh is on the wire. */
  fetching: boolean;
}): string {
  const at = new Date(asOf);
  const clock = new Date(now);
  const sameDay = dayInTz(at, tz) === dayInTz(clock, tz);
  const when = sameDay
    ? timeInTz(at, tz)
    : `${new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(at)}, ${timeInTz(at, tz)}`;
  const parts = [`As of ${when}`];
  const age = clock.getTime() - at.getTime();
  if (age > STALE_AFTER_MS) parts.push(ago(age));
  if (offline) parts.push('Offline');
  else if (fetching) parts.push('Refreshing…');
  return parts.join(' · ');
}

/** How long ago, in the app's one short spelling: "5 min ago", "3 h ago", "2 days ago". */
export function ago(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}
